import { Injectable, inject } from '@angular/core';
import { catchError, map, noop, Observable, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiRequest } from './requests';
import { API_BASEURL } from '../api.provider';

@Injectable()
export class ApiService {
  private _baseURL = inject<string>(API_BASEURL);
  private httpClient = inject(HttpClient);

  getUrl(postfix: string) {
    return this._baseURL + postfix;
  }

  call<TRequest, TResponse>(request: ApiRequest<TRequest, TResponse>): Observable<TResponse> {
    const url = this.getUrl(request.url);

    const payload = request.payload;
    const options = {
      headers: request.customHeaders,
      withCredentials: request.withCredentials,
    };
    switch (request.method) {
      case 'get':
        return this.httpClient
          .get<TResponse>(url, options)
          .pipe(
            request.mapFn ? map(request.mapFn) : tap(noop),
            request.catchFn ? catchError((e) => of(request.catchFn(e))) : tap(noop)
          );
      case 'post':
        return this.httpClient
          .post<TResponse>(url, payload, options)
          .pipe(
            request.mapFn ? map(request.mapFn) : tap(noop),
            request.catchFn ? catchError((e) => of(request.catchFn(e))) : tap(noop)
          );
      case 'delete':
        return this.httpClient
          .delete<TResponse>(url, { ...options, body: payload })
          .pipe(
            request.mapFn ? map(request.mapFn) : tap(noop),
            request.catchFn ? catchError((e) => of(request.catchFn(e))) : tap(noop)
          );
      case 'put':
        return this.httpClient
          .put<TResponse>(url, payload, options)
          .pipe(
            request.mapFn ? map(request.mapFn) : tap(noop),
            request.catchFn ? catchError((e) => of(request.catchFn(e))) : tap(noop)
          );
      default:
        throw Error('Unknown http method');
    }
  }

  callObserveResponse<TRequest, TResponse>(request: ApiRequest<TRequest, TResponse>) {
    const url = this.getUrl(request.url);
    const payload = request.payload;
    const options = { observe: 'response' as const, withCredentials: request.withCredentials };
    switch (request.method) {
      case 'get':
        return this.httpClient.get<TResponse>(url, options);
      case 'post':
        return this.httpClient.post<TResponse>(url, payload, options);
      case 'delete':
        return this.httpClient.delete<TResponse>(url, { ...options, body: payload });
      case 'put':
        return this.httpClient.put<TResponse>(url, payload, options);
      default:
        throw Error('Unknown http method');
    }
  }
}
